import type {
    ComparableData,
    ComparablesDataEntry,
    DataSource,
    MarketComparison,
    MonetaryDataValue,
    MoneyPerAreaDataValue,
    NumberDataValue,
    PercentageDataValue,
    SourceDate,
} from '@einwert/valuation-api-client'
import { ExhaustivenessError, getNormalizedFileName, monetaryToDecimal } from 'ComponentLibrary'
import { ComparisonDataEntry, ComparisonSource } from 'ComponentLibrary/src/molecules/ComparisonChart/types'

import {
    DATA_ENTRY_TYPE_VALUES_BY_MARKET_DATA_TYPE,
    getAutoConvertedFootNote,
    getValueLabel,
    observationTimeToDescription,
    retrievedOnDateToDescription,
    sourceDateToDescription,
} from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationMarketData/Comparables/constants'
import { isEmpty } from 'lodash'
import { TFunction } from 'i18next'

export type ComparableDataEntryWithIndex = ComparablesDataEntry & {
    isAutoConverted?: boolean
    index: number
}

export type ComparableDataWithIndex = {
    index: number
    source: DataSource
    entries: ComparableDataEntryWithIndex[]
}

export const filterMarketDataByMarketDataType = (
    marketData: ComparableData[],
    marketDataType: keyof MarketComparison,
    t: TFunction
): ComparableDataWithIndex[] =>
    marketData
        .map((marketData, sourceIndex) => ({
            ...marketData,
            index: sourceIndex,
            entries: addAutoConvertedEntries(
                marketData.entries?.map((entry, dataIndex) => ({ ...entry, index: dataIndex })) || [],
                t
            ).filter(({ data_type }) => DATA_ENTRY_TYPE_VALUES_BY_MARKET_DATA_TYPE[marketDataType].includes(data_type)),
        }))
        .filter((marketData) => !isEmpty(marketData.entries))

export const filterMarketDataByDataEntryType = (marketData: ComparableDataWithIndex[], dataEntryType: ComparablesDataEntry['data_type']) =>
    marketData
        .map((marketData) => ({
            ...marketData,
            entries: marketData.entries.filter(({ data_type }) => dataEntryType === data_type),
        }))
        .filter((marketData) => !isEmpty(marketData.entries))

const addAutoConvertedEntries = (entries: ComparableDataEntryWithIndex[], t: TFunction): ComparableDataEntryWithIndex[] => {
    const allEntries = [...entries]

    for (const entry of entries) {
        const convertedEntry = convertEntryIfPossible(entry, t)
        if (convertedEntry) {
            allEntries.push(convertedEntry)
        }
    }
    return allEntries
}

const convertEntryIfPossible = (entry: ComparableDataEntryWithIndex, t: TFunction): ComparableDataEntryWithIndex | undefined => {
    switch (entry.data_type) {
        case 'monthly_rent':
            return {
                ...entry,
                data_type: 'yearly_rent' as const,
                isAutoConverted: true,
                values: entry.values.map(({ value, ...rest }) => ({
                    ...rest,
                    value: { ...value, amount: value.amount * 12 },
                })),
            }
        case 'yearly_rent':
            return {
                ...entry,
                data_type: 'monthly_rent' as const,
                isAutoConverted: true,
                values: entry.values.map(({ value, ...rest }) => ({
                    ...rest,
                    value: { ...value, amount: value.amount / 12 },
                })),
            }
        case 'yield':
            return {
                ...entry,
                data_type: 'multiplier' as const,
                isAutoConverted: true,
                foot_note: getAutoConvertedFootNote(entry.foot_note, t),
                values: entry.values.map(({ value, ...rest }) => ({
                    ...rest,
                    value_type: 'float',
                    value: 1 / value,
                })),
            }
        case 'multiplier':
            return {
                ...entry,
                data_type: 'yield' as const,
                isAutoConverted: true,
                foot_note: getAutoConvertedFootNote(entry.foot_note, t),
                values: entry.values.map(({ value, ...rest }) => ({
                    ...rest,
                    value_type: 'percentage',
                    value: 1 / value,
                })),
            }
        case 'market_value':
        case 'market_area_value':
        case 'discount_rate':
        default:
            return undefined
    }
}

export const mapMarketDataToComparisonSource = (
    marketData: ComparableDataWithIndex[],
    valuationDate: string | undefined,
    t: TFunction,
    locale?: string
): ComparisonSource[] =>
    marketData.map(({ source, index: sourceIndex, entries }) => ({
        index: sourceIndex,
        publisher: source.publisher,
        title: source.title,
        notes: source.notes,
        region: source.region,
        sourceType: source.source_type,
        observationTime: observationTimeToDescription(source.observation_time, t, locale),
        url: source.source_type === 'web' ? source.url : source.source_type === 'file' ? source.file?.url || '' : undefined,
        urlName: source.source_type === 'web' ? source.url : source.source_type === 'file' ? getNormalizedFileName(source.file) : undefined,
        sourceDate:
            source.source_type === 'web'
                ? retrievedOnDateToDescription(source.retrieved_on, t, locale)
                : source.source_type === 'file'
                  ? sourceDateToDescription(source.source_date, t, locale)
                  : undefined,
        showSourceDateWarning:
            !!valuationDate &&
            (source.source_type === 'web'
                ? isMoreThanAYearSinceValuationDate(valuationDate, source.retrieved_on)
                : source.source_type === 'file'
                  ? getSourceDateWarning(valuationDate, source.source_date)
                  : false),
        entries: entries.map((entry) => mapEntry(entry, t, locale)),
    }))

const mapEntry = (entry: ComparableDataEntryWithIndex, t: TFunction, locale?: string): ComparisonDataEntry => ({
    index: entry.index,
    shortDescription: entry.short_description,
    fullDescription: entry.full_description,
    region: entry.region,
    isAutoConverted: entry.isAutoConverted,
    observationTime: observationTimeToDescription(entry.observation_time, t, locale),
    relevantNumberOfEntries: entry.statistics?.num_relevant_records,
    totalNumberOfEntries: entry.statistics?.num_total_records,
    footNote: entry.foot_note,
    values: entry.values.map((value) => mapValue(value, t)),
})

const mapValue = (
    { value_type, value, label }: MonetaryDataValue | MoneyPerAreaDataValue | PercentageDataValue | NumberDataValue,
    t: TFunction
) => {
    const labelText = getValueLabel(label, t)

    switch (value_type) {
        case 'monetary':
        case 'money_per_area':
            return { value: monetaryToDecimal(value) || 0, label: labelText }
        case 'percentage':
            return { value: value * 100, label: labelText }
        case 'float':
            return { value, label: labelText }
    }
}

const isMoreThanAYearSinceValuationDate = (valuationDate: string, date: string) => {
    const twelveMonthsSinceValuationDate = new Date(valuationDate)
    twelveMonthsSinceValuationDate.setMonth(twelveMonthsSinceValuationDate.getMonth() - 12)
    return new Date(date) < twelveMonthsSinceValuationDate
}

const getSourceDateWarning = (valuationDate: string, sourceDate?: SourceDate) => {
    if (!sourceDate) {
        return false
    }

    switch (sourceDate.date_type) {
        case 'day_month_year':
            return isMoreThanAYearSinceValuationDate(valuationDate, sourceDate.date)
        case 'month_year':
            return isMoreThanAYearSinceValuationDate(valuationDate, `${sourceDate.year}-${sourceDate.month}-28`)
        case 'year':
            return isMoreThanAYearSinceValuationDate(valuationDate, `${sourceDate.year}-12-31`)
        default:
            throw ExhaustivenessError(sourceDate)
    }
}
